import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { UIAgent } from "../../../types/Agent";
import { useFeatureAccess } from "../../../features/feature-access";
import {
  AGENT_TYPE,
  AGENT_TYPE_OPTIONS,
  isCommunicationAgentType,
} from "../../../constants/agentTypes";

interface AgentCardProps {
  agent: UIAgent;
  onAction: (agentId: string, action: string) => void;
}

const formatToolName = (toolName: string): string =>
  toolName
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

const formatAgentTypeLabel = (agentType?: string): string => {
  const raw = agentType?.trim();
  if (!raw) {
    return AGENT_TYPE_OPTIONS.find((o) => o.id === AGENT_TYPE.CONTENT_CREATOR)?.label ?? "Social Media Manager";
  }
  if (isCommunicationAgentType(raw)) return "AI Receptionist";
  const option = AGENT_TYPE_OPTIONS.find((o) => o.id === raw);
  if (option) return option.label;
  return formatToolName(raw);
};

const getInitials = (name: string): string => {
  const words = name.trim().split(/\s+/);
  if (words.length >= 2) return (words[0][0] + words[words.length - 1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
};

const getAvatarGradient = (initials: string): string => {
  const hash = initials.charCodeAt(0) + (initials.charCodeAt(1) || 0);
  const gradients = [
    "bg-gradient-to-br from-brand-primary-light to-brand-primary",
    "bg-gradient-to-br from-brand-secondary to-orange-400",
    "bg-gradient-to-br from-gray-500 to-gray-700",
    "bg-gradient-to-br from-blue-400 to-indigo-600",
    "bg-gradient-to-br from-pink-400 to-rose-600",
    "bg-gradient-to-br from-teal-400 to-teal-600",
    "bg-gradient-to-br from-blue-600 to-blue-800",
  ];
  return gradients[hash % gradients.length];
};

const CAPABILITIES_BY_TYPE: Record<string, string[]> = {
  content_creator: [
    "Creates posts & captions for all platforms",
    "Generates stunning AI images",
    "Schedules & publishes content",
    "Tracks reach, engagement & post performance",
  ],
  ugc_video: [
    "Creates UGC-style video scripts",
    "Adapts tone & format per platform",
    "Generates hooks & call-to-actions",
    "Optimized for Reels, TikTok & Shorts",
  ],
  communication_agent_email: [
    "Answers customer questions instantly",
    "Sends booking links in chat",
    "Follows up with leads automatically",
    "Responds via Email",
  ],
  communication_agent_sms: [
    "Answers customer questions instantly",
    "Sends booking links in chat",
    "Follows up with leads automatically",
    "Responds via SMS",
  ],
  general: [
    "Answers questions about your business",
    "Helps with research, drafting & summaries",
    "Generates AI images on request",
    "No fixed role — ask it anything",
  ],
  visibility_manager: [
    "Reads your real Google Search performance",
    "Finds where you can rank higher & get cited in AI search",
    "Drafts & publishes AI-ready content to win recommendations",
    "Checks if ChatGPT, Gemini & Perplexity recommend you",
  ],
};

const getCapabilities = (agentType?: string): string[] => {
  if (!agentType) return CAPABILITIES_BY_TYPE.content_creator;
  if (isCommunicationAgentType(agentType)) {
    const key = agentType.trim().toLowerCase();
    return CAPABILITIES_BY_TYPE[key] ?? CAPABILITIES_BY_TYPE.communication_agent_email;
  }
  return CAPABILITIES_BY_TYPE[agentType.trim().toLowerCase()] ?? [];
};

const relativeTime = (iso?: string): string | null => {
  if (!iso) return null;
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};



const AgentCard: React.FC<AgentCardProps> = ({ agent, onAction }) => {
  const [capsOpen, setCapsOpen] = React.useState(false);
  const [menuOpen, setMenuOpen] = React.useState(false);

  const avatarStyle =
    agent.avatar && agent.avatar !== "/avatars/default-avatar.png" && agent.avatar !== ""
      ? { backgroundImage: `url('${agent.avatar}')` }
      : {};

  const initials = getInitials(agent.name);
  const avatarGradient = getAvatarGradient(initials);
  const hasImage = !!avatarStyle.backgroundImage;
  const caps = getCapabilities(agent.agent_type);
  // Starter Brands have this agent labelled Pro (SGL-449).
  const { canAccessFeature } = useFeatureAccess();
  const proLocked =
    agent.agent_type === AGENT_TYPE.VISIBILITY_MANAGER && !canAccessFeature("visibility_manager");
  const toolCount = agent.tools?.length ?? 0;

  const TOOL_PILL_LIMIT = 3;
  const visibleTools = agent.tools?.slice(0, TOOL_PILL_LIMIT) ?? [];
  const extraToolCount = toolCount - TOOL_PILL_LIMIT;

  // Clone is currently supported for bedrock_claude agents only (OpenAI agents
  // own a vector store that can't be safely shared). Delete applies to every
  // agent, so the ⋮ menu is now always rendered and each item gates itself.
  const canClone = agent.provider === "bedrock_claude";

  return (
    <div className="group flex flex-col rounded-xl border border-theme-border bg-theme-surface shadow-sm transition-all duration-200 hover:shadow-md">

      {/* Header */}
      <div className="flex items-start justify-between px-5 pt-5 pb-4">
        <div className="flex items-center gap-3">
          <div
            className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-[15px] font-bold text-white shadow-sm ${hasImage ? "bg-cover bg-center" : avatarGradient}`}
            style={hasImage ? avatarStyle : undefined}
          >
            {!hasImage && initials}
          </div>
          <div>
            <h3 className="text-[15px] font-bold leading-tight text-theme-fg group-hover:text-brand-primary transition-colors">
              {agent.name}
              {proLocked && (
                <span className="ml-2 rounded-full bg-brand-primary/10 px-2 py-0.5 align-middle text-[11px] font-semibold text-brand-primary">
                  Pro
                </span>
              )}
            </h3>
            {relativeTime(agent.updated_at) && (
              <span className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-theme-fg-muted">
                Updated {relativeTime(agent.updated_at)}
              </span>
            )}
          </div>
        </div>
        <div className="relative">
            <button
              type="button"
              onClick={() => setMenuOpen((o) => !o)}
              className={`transition-colors ${menuOpen ? "text-brand-primary" : "text-theme-fg-muted hover:text-brand-primary"}`}
              aria-label="More options"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
            >
              <span className="material-icons-outlined text-[20px]">more_vert</span>
            </button>

            {/* Backdrop — closes the menu when clicking outside (no animation needed; it's transparent) */}
            {menuOpen && (
              <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
            )}

            {/* Floating menu — smooth scale/fade growing from the top-right anchor */}
            <AnimatePresence>
              {menuOpen && (
                <motion.div
                  key="agent-card-menu"
                  role="menu"
                  initial={{ opacity: 0, scale: 0.9, y: -10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: -8, transition: { duration: 0.14, ease: [0.4, 0, 1, 1] } }}
                  transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
                  className="absolute right-0 top-full z-20 mt-1.5 min-w-[170px] overflow-hidden rounded-xl bg-theme-surface py-1.5 shadow-xl ring-1 ring-black/5"
                  style={{ border: '1px solid rgba(0,0,0,0.06)', transformOrigin: 'top right', willChange: 'transform, opacity' }}
                >
                  {canClone && (
                    <motion.button
                      type="button"
                      role="menuitem"
                      onClick={() => { setMenuOpen(false); onAction(agent.id, "clone"); }}
                      initial={{ opacity: 0, x: 4 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.06, duration: 0.18, ease: "easeOut" }}
                      className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-[13px] font-medium text-theme-fg transition-colors hover:bg-black/[0.04]"
                    >
                      <span className="material-icons-outlined text-[16px] text-brand-primary">content_copy</span>
                      Clone agent
                    </motion.button>
                  )}
                  <motion.button
                    type="button"
                    role="menuitem"
                    onClick={() => { setMenuOpen(false); onAction(agent.id, "delete"); }}
                    initial={{ opacity: 0, x: 4 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: canClone ? 0.1 : 0.06, duration: 0.18, ease: "easeOut" }}
                    className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-[13px] font-medium text-theme-danger transition-colors hover:bg-black/[0.04]"
                  >
                    <span className="material-icons-outlined text-[16px]">delete_outline</span>
                    Delete agent
                  </motion.button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
      </div>

      {/* Info strip — Type / Model / Tools */}
      <div className="grid grid-cols-3 px-5 pb-1">
        <div className="flex flex-col gap-1">
          <span className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-theme-fg-muted">
            <span className="material-icons-outlined text-[12px]">category</span>
            Type
          </span>
          <span className="text-[12px] font-bold text-theme-fg leading-tight">{formatAgentTypeLabel(agent.agent_type)}</span>
        </div>
        <div className="flex flex-col gap-1">
          <span className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-theme-fg-muted">
            <span className="material-icons-outlined text-[12px]">psychology</span>
            Model
          </span>
          <span className="text-[12px] font-bold text-theme-fg leading-tight truncate" title={agent.model}>{agent.model || "—"}</span>
        </div>
        <div className="flex flex-col gap-1">
          <span className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-theme-fg-muted">
            <span className="material-icons-outlined text-[12px]">construction</span>
            Tools
          </span>
          <span className="text-[12px] font-bold text-theme-fg leading-tight">
            {toolCount > 0 ? `${toolCount} tool${toolCount > 1 ? "s" : ""}` : "None"}
          </span>
        </div>
      </div>

      {/* Tool pills */}
      {visibleTools.length > 0 && (
        <div className="flex flex-wrap gap-1.5 px-5 pt-2 pb-1">
          {visibleTools.map((tool) => (
            <span
              key={tool}
              className="inline-flex items-center rounded-full border bg-theme-background px-2.5 py-0.5 text-[11px] font-medium text-theme-fg"
              style={{ borderColor: 'rgba(0,0,0,0.08)' }}
            >
              {formatToolName(tool)}
            </span>
          ))}
          {extraToolCount > 0 && (
            <span
              className="inline-flex items-center rounded-full border bg-theme-background px-2.5 py-0.5 text-[11px] font-medium text-theme-fg-muted"
              style={{ borderColor: 'rgba(0,0,0,0.08)' }}
            >
              +{extraToolCount}
            </span>
          )}
        </div>
      )}

      {/* Capabilities — floating overlay dropdown */}
      {caps.length > 0 && (
        <div className="relative" style={{ borderTop: '1px solid rgba(0,0,0,0.06)' }}>
          <button
            type="button"
            onClick={() => setCapsOpen((o) => !o)}
            className="flex w-full items-center justify-between px-5 py-3 text-left transition-colors hover:bg-black/[0.02]"
          >
            <span className="flex items-center gap-1.5 text-[12px] font-semibold text-theme-fg">
              <span className="material-icons-outlined text-[15px] text-brand-primary">bolt</span>
              {caps.length} capabilities
            </span>
            <span
              className="material-icons-outlined text-[18px] text-theme-fg-muted transition-transform duration-200"
              style={{ transform: capsOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
            >
              expand_more
            </span>
          </button>

          {capsOpen && (
            <>
              {/* Backdrop — closes dropdown when clicking outside */}
              <div className="fixed inset-0 z-10" onClick={() => setCapsOpen(false)} />
              {/* Floating panel */}
              <div
                className="absolute left-0 right-0 z-20 rounded-b-xl bg-theme-surface px-5 py-3 shadow-lg"
                style={{ border: '1px solid rgba(0,0,0,0.08)', borderTop: 'none' }}
              >
                <ul className="flex flex-col gap-1.5">
                  {caps.map((cap) => (
                    <li key={cap} className="flex items-center gap-2">
                      <span className="material-icons text-[14px] text-brand-primary shrink-0">check_circle</span>
                      <span className="text-[11.5px] font-medium text-theme-fg">{cap}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </>
          )}
        </div>
      )}

      {/* Action buttons */}
      <div className="grid grid-cols-2 gap-2 px-5 py-4 mt-auto" style={{ borderTop: '1px solid rgba(0,0,0,0.06)' }}>
        <button
          type="button"
          onClick={() => { setCapsOpen(false); onAction(agent.id, "edit"); }}
          className="flex items-center justify-center gap-1.5 rounded-lg border px-4 py-2 text-[13px] font-medium text-theme-fg transition-colors hover:border-brand-primary/40 hover:bg-theme-secondary/10"
          style={{ borderColor: 'rgba(0,0,0,0.1)' }}
        >
          <span className="material-icons-outlined text-[15px]">edit</span>
          Edit
        </button>
        <button
          type="button"
          onClick={() => { setCapsOpen(false); onAction(agent.id, "chat"); }}
          className="flex items-center justify-center gap-1.5 rounded-lg bg-brand-primary px-4 py-2 text-[13px] font-medium text-white shadow-sm transition-colors hover:bg-brand-primary-hover"
        >
          <span className="material-icons-outlined text-[15px]">chat_bubble_outline</span>
          Chat
        </button>
      </div>
    </div>
  );
};

export default AgentCard;
